/**
 * Trusted enrollment issuers wire shapes.
 *
 * These are the external OIDC issuers (e.g. a Gatekeeper/Keystone deployment)
 * that PureID trusts to sign passkey-enrollment request objects for a tenant.
 * The signed request object's `iss` must exactly match a registered issuer, its
 * signature is verified against the registered `jwks_uri`, and its `aud` must
 * equal the registered `audience` (this tenant's PureID issuer). Establishing
 * this trust is a PLATFORM-admin act.
 *
 *   GET  /api/v1/tenants/{tenant_id}/trusted-enrollment-issuers   list
 *   POST /api/v1/tenants/{tenant_id}/trusted-enrollment-issuers   register
 */

import { z } from "zod";
import { trustedEnrollmentIssuerSchema, trustedEnrollmentIssuersResponseSchema } from "./api.generated";

// Responses are defined by the API contract (TrustedEnrollmentIssuer) and generated.
export { trustedEnrollmentIssuerSchema, trustedEnrollmentIssuersResponseSchema };
export type TrustedEnrollmentIssuer = z.infer<typeof trustedEnrollmentIssuerSchema>;
export type TrustedEnrollmentIssuersResponse = z.infer<typeof trustedEnrollmentIssuersResponseSchema>;

// Register a new trusted issuer. All four scalars are required; the backend
// forces `enabled=true` on create and re-validates jwks_uri / return URLs, so
// they are not part of the input. No `.default()` here — the dialog uses
// controlled state + safeParse, and defaults live in component state (the RHF
// convention only bans defaults inside a resolver, but keeping it uniform).
export const createTrustedEnrollmentIssuerSchema = z.object({
  display_name: z
    .string()
    .min(1, "A display name is required")
    .max(64, "Display name is too long"),
  // The issuer is an identifier, not necessarily fetched — it must match the
  // `iss` the signing service puts in the request object byte-for-byte.
  issuer: z.string().min(1, "Issuer is required"),
  // jwks_uri IS fetched server-side, so it must be a URL (https, or http for a
  // loopback / localdev host — the backend enforces the exact policy).
  jwks_uri: z.string().url("Must be a valid URL"),
  // The audience is this tenant's PureID issuer identifier.
  audience: z.string().min(1, "Audience is required"),
  return_url_allowlist: z.array(z.string().url("Must be a valid URL")),
});
export type CreateTrustedEnrollmentIssuerInput = z.infer<
  typeof createTrustedEnrollmentIssuerSchema
>;

// Update an existing trusted issuer. Same editable scalars as create, plus
// `enabled` — editing is the one place an admin can disable an issuer without
// deleting it (create always forces enabled=true). The id is a path param, not
// part of the body.
export const updateTrustedEnrollmentIssuerSchema =
  createTrustedEnrollmentIssuerSchema.extend({
    enabled: z.boolean(),
  });
export type UpdateTrustedEnrollmentIssuerInput = z.infer<
  typeof updateTrustedEnrollmentIssuerSchema
>;
